// Country + dataset catalog for the composer's data picker. Mirrors the
// desktop app (analystbro-desktop/src/datasets.ts): the same server catalog,
// the same per-chat selection and the same [Context] line ahead of each
// question, so a question asked here runs exactly like one typed on desktop.
import { API_BASE_URL } from '../config';

export const KIND_LABEL = {
  indexed: 'Searchable',
  file_index: 'File libraries',
  live_source: 'Live sources',
};

const COUNTRY_META = {
  BD: { name: 'Bangladesh', flag: '🇧🇩' },
  US: { name: 'United States', flag: '🇺🇸' },
  IN: { name: 'India', flag: '🇮🇳' },
  GB: { name: 'United Kingdom', flag: '🇬🇧' },
  SA: { name: 'Saudi Arabia', flag: '🇸🇦' },
  GLOBAL: { name: 'Global', flag: '🌐' },
};

// Shown only when the server call fails.
const FALLBACK = [
  {
    code: 'BD', name: 'Bangladesh', flag: '🇧🇩',
    datasets: [
      { slug: 'dse-annual-reports', name: 'DSE company reports', kind: 'indexed', description: 'Annual & quarterly reports, PSI and credit ratings of DSE-listed companies', defaultSelected: true },
      { slug: 'bd-secbd-regulation', name: 'BSEC regulation', kind: 'file_index', description: 'Securities & Exchange Commission laws, rules, directives and notifications', defaultSelected: true },
      { slug: 'bd-bbs-publications', name: 'BBS publications', kind: 'file_index', description: 'Bangladesh Bureau of Statistics statistical publications (PDF)', defaultSelected: false },
      { slug: 'bd-bb-publications', name: 'Bangladesh Bank publications', kind: 'file_index', description: 'Weekly → annual BB reports and statistical bulletins (PDF / Excel)', defaultSelected: false },
      { slug: 'bd-bb-excel-series', name: 'Bangladesh Bank data series', kind: 'file_index', description: 'GDP, CPI, money, rates, BOP, reserves, trade — Excel + SDMX', defaultSelected: false },
      { slug: 'bd-bb-data-sources', name: 'Bangladesh Bank live data pages', kind: 'live_source', description: 'Exchange rates, reserves, remittances, auctions, call money', defaultSelected: false },
    ],
  },
];

export let COUNTRIES = FALLBACK;
let loading = null;

/** Fetch the catalog once per page load, grouped by country. */
export function loadCatalog() {
  if (!loading) {
    loading = fetch(`${API_BASE_URL}/api/datasets/`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((res) => {
        const byCode = new Map();
        for (const r of res.datasets ?? []) {
          const meta = COUNTRY_META[r.country] ?? { name: r.country, flag: '🏳️' };
          const c = byCode.get(r.country) ?? { code: r.country, ...meta, datasets: [] };
          c.datasets.push({
            slug: r.slug, name: r.name, kind: r.kind, description: r.description,
            defaultSelected: r.default_selected, itemCount: r.item_count ?? undefined,
          });
          byCode.set(r.country, c);
        }
        if (byCode.size) COUNTRIES = [...byCode.values()];
        return COUNTRIES;
      })
      .catch(() => COUNTRIES);
  }
  return loading;
}

export const EMPTY_SELECTION = { country: null, datasets: [] };
export const countryOf = (code) => COUNTRIES.find((c) => c.code === code) ?? null;
export const defaultsFor = (code) =>
  countryOf(code)?.datasets.filter((d) => d.defaultSelected).map((d) => d.slug) ?? [];

/** The line sent ahead of a question. Same wording as the desktop's. */
export function contextLine(sel) {
  const c = countryOf(sel?.country);
  const known = (sel?.datasets ?? []).filter((s) => c?.datasets.some((d) => d.slug === s));
  if (!c || known.length === 0) return '';
  return (
    `[Context] Country: ${c.name} (${c.code}) · Datasets: ${known.join(', ')}` +
    ` — for filings, reports and publications, search these datasets via the analystbro tools (pdf_search/pdf_list with collections, list_dataset_files);` +
    ` the chart, screener and price tools are always available and need no dataset;` +
    ` no web search unless I ask; if the datasets lack a document, say so and name the dataset that would have it (list_datasets).`
  );
}

const CONTEXT_RE = /^\[Context\][^\n]*\n*/;
/** Drop a leading context line, for showing a stored user message. */
export const stripContext = (text) => (typeof text === 'string' ? text.replace(CONTEXT_RE, '') : text);

// Per-chat selection; the last country used seeds new chats.
const key = (chatId) => `ab.dataSel.${chatId}`;

export function loadSelection(chatId) {
  try {
    const raw = chatId && localStorage.getItem(key(chatId));
    if (raw) return JSON.parse(raw);
    const last = localStorage.getItem('ab.lastCountry');
    if (last && countryOf(last)) return { country: last, datasets: defaultsFor(last) };
  } catch { /* fall through */ }
  return EMPTY_SELECTION;
}

export function saveSelection(chatId, sel) {
  try {
    if (chatId) localStorage.setItem(key(chatId), JSON.stringify(sel));
    if (sel.country) localStorage.setItem('ab.lastCountry', sel.country);
    else localStorage.removeItem('ab.lastCountry');
  } catch { /* non-fatal */ }
}
